/*
 * File: DemographicsModel.js
 * Project: mp-mobile-app
 *
 * Created by Jennifer Loecker on 09/17/2019
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: mobx-state-tree model for a patient
 */
import { types, getSnapshot, flow } from "mobx-state-tree";
import DemographicsDataModel from "./DemographicsDataModel";
import PatientApi from "./DemographicsApi";
import mstIsNumber from "utils/MSTIsNumber";
import { sexTypes } from "services/references/References";
import DateFormatter from "utils/DateFormatter";
import PhoneModel from "./PhoneModel";

const PatientModel = types
  .model("PatientModel", DemographicsDataModel)

  .actions(self => ({
    updateNewPatient(loadedPatient) {
      self.accountId = loadedPatient.accountId;
    },

    resetAccount() {
      self.accountId = undefined;
    },

    update(key, val) {
      if (key === "accountProvider" || key === "patientProvider") {
        let submodel = self[key];
        let newVal = Number(val);
        submodel["userId"] = newVal;
      } else if (key === "patientOffice" || key === "feeSchedule") {
        let submodel = self[key];
        let newVal = Number(val);
        submodel["id"] = newVal;
      } else self[key] = mstIsNumber(self, key, val);
    },

    updatePhone(key, val) {
      if (!self[key]) {
        self[key] = PhoneModel.create();
      }
      self[key].number = val;
    },

    updatePhoneType(key, val) {
      var phoneModel;
      if (key === "phone1Type") {
        if (!self.phone1) {
          self.phone1 = PhoneModel.create();
        }
        phoneModel = self.phone1;
      } else if (key === "phone2Type") {
        if (!self.phone2) {
          self.phone2 = PhoneModel.create();
        }
        phoneModel = self.phone2;
      } else if (key === "phone3Type") {
        if (!self.phone3) {
          self.phone3 = PhoneModel.create();
        }
        phoneModel = self.phone3;
      }
      phoneModel.setType(mstIsNumber(phoneModel, "type", val));
    },

    /**
     * Fixes the phone object to undefined if phone number is blank
     */
    preSave() {
      if (self.phone1 && !self.phone1.number) self.phone1 = undefined;
      if (self.phone2 && !self.phone2.number) self.phone2 = undefined;
      if (self.phone3 && !self.phone3.number) self.phone3 = undefined;
      if (self.zipCode) self.zipCode = self.zipCode.trim();
      if (self.city) self.city = self.city.trim();
      if (self.country) self.country = self.country.trim();
      if (self.state) self.state = self.state.trim();
      if (self.streetAddress) self.streetAddress = self.streetAddress.trim();
      if (self.suite) self.suite = self.suite.trim();
    },

    saveNewPatient: flow(function* () {
      self.preSave();
      try {
        let patientSnapshot = Object.assign({}, getSnapshot(self));
        patientSnapshot.accountProviderId = self.accountProvider.userId;
        patientSnapshot.patientProviderId = self.patientProvider.userId;
        patientSnapshot.officeId = self.patientOffice.id;
        patientSnapshot.feeScheduleId = self.feeSchedule.id;
        if (patientSnapshot.birthdate)
          patientSnapshot.birthdate = DateFormatter.forApi(
            new Date(patientSnapshot.birthdate)
          );
        return yield PatientApi.create(patientSnapshot);
      } catch (error) {
        console.log("Error adding new patient to the api from model", error);
      }
    }),

    updatePatient: flow(function* () {
      self.preSave();
      try {
        let snapshot = Object.assign({}, getSnapshot(self));
        if (snapshot.birthdate)
          snapshot.birthdate = DateFormatter.forApi(
            new Date(snapshot.birthdate)
          );
        return yield PatientApi.update(snapshot);
      } catch (error) {
        console.log("Error updating patient to the api from model", error);
      }
    }),

    // inserts default patient information into corresponding fields
    setDefaultPatientInfo: flow(function* () {
      let defaultPatient = yield PatientApi.defaultPatient();
      self.update("patientProvider", defaultPatient.providerId);
      self.update("patientOffice", defaultPatient.officeId);
      self.update("feeSchedule", defaultPatient.feeScheduleId);
      self.update("maritalStatus", defaultPatient.maritalStatus);
      self.update("sex", defaultPatient.sex);
      self.update("employment", defaultPatient.employment);
      self.update("preferredLanguageId", defaultPatient.languageId);
      self.update(
        "relationshipToPrimary",
        defaultPatient.relationshipToPrimary
      );
      self.update(
        "relationshipToSecondary",
        defaultPatient.relationshipToSecondary
      );
      self.phone1 = PhoneModel.create(defaultPatient.phone1);
      self.phone2 = PhoneModel.create(defaultPatient.phone2);
      self.phone3 = PhoneModel.create(defaultPatient.phone3);
      self.update("accountProvider", defaultPatient.accountProviderId);
    })
  }))

  .views(self => ({
    patientName() {
      return self.firstName + " " + self.lastName + " (" + self.displayId + ")";
    },

    // return formatted string of full name
    get fullName() {
      let fullName = "";
      let lastName = self.lastName;
      let firstName = self.firstName;
      let middleName = self.middle;
      let nickname = self.nickname;

      // check if each section of the name exists
      if (lastName) {
        fullName += lastName + ", ";
      }
      if (firstName) {
        fullName += firstName + " ";
      }
      if (middleName) {
        fullName += middleName + " ";
      }
      if (nickname) {
        fullName += "(" + nickname + ")";
      }
      return fullName;
    },
    birthDayString() {
      return DateFormatter.birthDate(self.birthdate);
    },

    patientSex() {
      // convert to a string because "0" === false
      if (String(self.sex)) {
        let sex = sexTypes.find(r => r.id === self.sex);
        if (sex) return sex.name;
        return "";
      }
      return "Unknown";
    }
  }));

export default PatientModel;
